import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import ToastService from '~/services/toast.service';

import { i18n } from '~/utils';

import { queryKeysInvoiceReprocessing } from '../queryKeys';

import { sendInvoicePreview } from './sendInvoicePreview';
import {
  type SendInvoicePreviewParams,
  type SendInvoicePreviewResponse,
} from './types';

/**
 * A custom hook to send a preview of a reprocessing invoice that the user has created using the create forwarded invoice preview endpoint.
 * Uses the `useMutation` hook from `@tanstack/react-query` internally.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/send_invoice_preview_invoice_process__process_id__invoice_preview__preview_id__send_post
 */
export function useMutationSendInvoicePreview(
  options?: UseMutationOptions<
    SendInvoicePreviewResponse,
    Error,
    SendInvoicePreviewParams
  >,
) {
  const queryClient = useQueryClient();

  return useMutation<
    SendInvoicePreviewResponse,
    Error,
    SendInvoicePreviewParams
  >({
    mutationFn: sendInvoicePreview,
    onError(_error) {
      return; // prevent error from bubbling up further unless overridden via mutation options
    },
    onSettled(_data, _error, { processId }) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceReprocessing.getAll({}),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceReprocessing.get(processId),
      });
    },
    onSuccess() {
      ToastService.success(
        i18n.t(($) => $.sendInvoicePreview.success, {
          ns: 'data',
        }),
      );
    },
    ...options,
  });
}
